import React, { useEffect, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import styled from "styled-components";

import { OpenInfoIcon } from "../../../common/components/core/icons";
import { useSelector, useDispatch } from "../../../redux/hooks";
import { currentRolesSelector, currentUserAccountNameSelector } from "../../../redux/slices/auth/selectors";
import { getContactsByAccountNames } from "../../../redux/slices/accountInfo/thunks";
import { BesuClaimListItem } from "../../../types/claim";
import { Contact } from "../../../types/common";
import { BesuProjectParty, BesuProjectState } from "../../../types/project";
import { formatToDate } from "../../../utils/common";
import {
    isUserArchitect,
    isUserContractor,
    isNodeDeveloper,
} from "../../../utils/role";
import useOpenEntityInfoHandler from "../hooks/useOpenEntityInfoHandler";
import useOpenContractInfoHandler from "../hooks/useOpenContractInfoHandler";

type ProjectInfoProps = {
    project: BesuProjectState;
    showClaim?: boolean;
    claims?: BesuClaimListItem[];
    currentContact?: Contact;
};

const ProjectInfo = ({
    project,
    showClaim,
    claims,
    currentContact,
}: ProjectInfoProps): JSX.Element => {
    const currentRoles = useSelector(currentRolesSelector);
    const currentUserAccountName = useSelector(currentUserAccountNameSelector);

    const [contactMap, setContactMap] = useState<Map<string, Contact>>();
    const dispatch = useDispatch();
    useEffect(() => {
        if (!isUserContractor(currentRoles)) {
            const accountList = project.mainContractors.map(con => con.displayName);
            const getContactInfos = async () => {
                const response = await dispatch(getContactsByAccountNames(accountList)).unwrap();
                const map = new Map<string, Contact>();
                response.forEach(party => {
                    const contact: Contact = {
                        company: party.name,
                        email: party.contactEmail,
                        contactPerson: party.contactPerson,
                        phone: party.contactNumber,
                    };
                    map.set(party.accountName, contact);
                })
                setContactMap(map);
            }
            getContactInfos();
        }
        return () => {
            setContactMap(undefined);
        };
    }, [currentRoles, project, dispatch]);

    const [claimMap, setClaimMap] = useState<Map<string, BesuClaimListItem[]>>();
    useEffect(() => {
        const getClaimMap = (): Map<string, BesuClaimListItem[]> => {
            const map = new Map();
            if (isNodeDeveloper()) {
                project.mainContractors.forEach(contractor => {
                    map.set(contractor.accountName, []);
                })
            } else {
                map.set(currentUserAccountName, []);
            }
            let claimList = claims;
            if (!claimList) {
                return map;
            }
            if (!isNodeDeveloper()) {
                claimList = claimList.filter((claim) => {
                    return currentUserAccountName === claim.contractorAccountName || currentUserAccountName === claim.contractorDisplayName;
                });
            }
            claimList.forEach((claim) => {
                const contractorAccountName = claim.contractorAccountName;
                if (!map.has(contractorAccountName)) {
                    map.set(contractorAccountName, [])
                }
                map.get(contractorAccountName).push(claim);
            });
            return map;
        }

        const claimMap = getClaimMap();
        setClaimMap(claimMap)
    }, [claims, currentUserAccountName, project.mainContractors]);

    const { handler: openDeveloperInfo } = useOpenEntityInfoHandler(
        currentContact ?? toEmptyContact(project.developer),
    );
    const { handler: openContractorInfo } = useOpenEntityInfoHandler(
        currentContact ?? toEmptyContact(project.mainContractors[0]),
    );
    const { handler: openArchitectInfo } = useOpenEntityInfoHandler(
        currentContact ?? toEmptyContact(project.architect),
    );

    const { handler: openContractInfo } = useOpenContractInfoHandler();

    return (
        <div>
            {!isNodeDeveloper() && (
                <Row xs={4}>
                    {!isNodeDeveloper() && (
                        <EntityColumn
                            header="Developer"
                            entityName={currentContact?.company ?? project.developer?.displayName ?? project.developer?.accountName ?? ""}
                            onOpen={openDeveloperInfo}
                        />
                    )}
                    {!isUserContractor(currentRoles) && (
                        <EntityColumn
                            header="Main Contractor"
                            entityName={project.mainContractors[0]?.displayName ?? project.mainContractors[0]?.accountName ?? ""}
                            onOpen={openContractorInfo}
                        />
                    )}
                    {isUserArchitect(currentRoles) && (
                        <EntityColumn
                            header="Architect"
                            entityName={project.architect?.displayName ?? project.architect?.accountName ?? ""}
                            onOpen={openArchitectInfo}
                        />
                    )}
                    <Col
                        className={`px-4 ${showClaim ? "border-end border-humble-grey" : ""
                            }`}
                    >
                        <p className="mt-2 mb-2 text-label-grey">Creation Date</p>
                        <p className="fw-bold">{formatToDate(project.createdAt)}</p>
                    </Col>
                    {showClaim && claims && (
                        <Col className="px-4">
                            <p className="mt-2 mb-2 text-label-grey">
                                Number of Claims
                            </p>
                            <p className="fw-bold">{claims.length}</p>
                        </Col>
                    )}
                </Row>
            )}
            {
                isNodeDeveloper() && claimMap && (
                    <div>
                        <Row xs={4}>
                            <Col className="border-end border-humble-grey">
                                <p className="mt-2 mb-2 text-label-grey">Service Provider</p>
                            </Col>
                            <Col
                                className={`px-4 ${showClaim ? "border-end border-humble-grey" : ""
                                    }`}
                            >
                                <p className="mt-2 mb-2 text-label-grey">Creation Date</p>
                            </Col>
                            {isUserArchitect(currentRoles) && (
                                <Col className="border-end border-humble-grey">
                                    <p className="mt-2 mb-2 text-label-grey">Architect</p>
                                </Col>
                            )}
                            {showClaim && claims && (
                                <Col className="px-4">
                                    <p className="mt-2 mb-2 text-label-grey">
                                        Number of Claims
                                    </p>
                                </Col>
                            )}
                        </Row>
                        {
                            claimMap && contactMap && Array.from(claimMap).map((object, index) => {
                                const serviceProvider = object[0]
                                const claimList = object[1]
                                return (
                                    <Row xs={4} key={index}>
                                        <Col className="border-end border-humble-grey">
                                            <p className="fw-bold">
                                                {contactMap.get(serviceProvider)?.company}
                                                <OpenInfoButton
                                                    aria-label="expand details"
                                                    variant="link"
                                                    className="px-1 py-0 border-0 lh-1"
                                                    onClick={
                                                        () => {
                                                            const contact = contactMap.get(serviceProvider) ?? toEmptyContact(project.mainContractors.find(contractor => contractor.accountName === serviceProvider))
                                                            openContractInfo(contact)
                                                        }
                                                    }
                                                >
                                                    <OpenInfoIcon className="icon-small" />
                                                </OpenInfoButton>
                                            </p>
                                        </Col>
                                        <Col className={`px-4 ${showClaim ? "border-end border-humble-grey" : ""
                                            }`}>
                                            <p className="fw-bold">{formatToDate(project.createdAt)}</p>
                                        </Col>
                                        {isUserArchitect(currentRoles) && (
                                            <Col className="border-end border-humble-grey">
                                                <p className="fw-bold">
                                                    {isNodeDeveloper()
                                                        ? (project.architect?.displayName ?? project.architect?.accountName ?? "")
                                                        : (project.architect?.displayName ?? project.architect?.accountName ?? "")}
                                                    <OpenInfoButton
                                                        aria-label="expand details"
                                                        variant="link"
                                                        className="px-1 py-0 border-0 lh-1"
                                                        onClick={() => {
                                                            openArchitectInfo()
                                                        }
                                                        }
                                                    >
                                                        <OpenInfoIcon className="icon-small" />
                                                    </OpenInfoButton>
                                                </p>
                                            </Col>
                                        )}
                                        {showClaim && claimList && (
                                            <Col className="px-4">
                                                <p className="fw-bold">{claimList.length}</p>
                                            </Col>
                                        )}
                                    </Row>
                                )
                            })
                        }
                    </div>
                )
            }
        </div>
    );
};

const toEmptyContact = (party?: BesuProjectParty | null): Contact => ({
    company: party?.displayName ?? "",
    contactPerson: "",
    email: "",
    phone: "",
});

type EntityColumnProps = {
    header: string;
    entityName: string;
    onOpen: () => void;
};

const EntityColumn = ({
    header,
    entityName,
    onOpen,
}: EntityColumnProps): JSX.Element => {
    const ariaLabel = "expand " + header + "-" + entityName + " details";
    return (
        <Col className="border-end border-humble-grey">
            <p className="mt-2 mb-2 text-label-grey">{header}</p>
            <p className="fw-bold">
                {entityName}
                <OpenInfoButton
                    aria-label={ariaLabel}
                    title={ariaLabel}
                    variant="link"
                    className="px-1 py-0 border-0 lh-1"
                    onClick={onOpen}
                >
                    <OpenInfoIcon className="icon-small" />
                </OpenInfoButton>
            </p>
        </Col>
    );
};

const OpenInfoButton = styled(Button)`
    min-width: unset;
`;

export default ProjectInfo;
